/**
 * SMSTab - SMS Management (Refactored)
 * Main orchestrator for SMS compose, templates, scheduling, and history
 */
import { useState, useEffect, useCallback } from 'react';
import { Send, FileText, Calendar, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Import sub-components
import {
  SMSStatsHeader,
  SMSComposeView,
  SMSTemplatesView,
  SMSScheduledView,
  SMSHistoryView,
  SMSConfirmDialog,
  SMSTemplateDialog
} from './sms';

export default function SMSTab({ authHeaders = {} }) {
  // View state
  const [activeView, setActiveView] = useState('compose');
  
  // Message composition state
  const [message, setMessage] = useState('');
  const [recipientType, setRecipientType] = useState('campaign');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [campaignId, setCampaignId] = useState('all');
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    content: '',
    category: 'general'
  });
  
  // Scheduling state
  const [scheduledSMS, setScheduledSMS] = useState([]);
  const [scheduleDateTime, setScheduleDateTime] = useState('');
  const [isScheduling, setIsScheduling] = useState(false);
  
  // History state
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [batches, setBatches] = useState([]);
  
  // Stats state
  const [stats, setStats] = useState({ today_sent: 0, month_sent: 0, total_sent: 0 });
  const [deliveryStats, setDeliveryStats] = useState({ delivered: 0, pending: 0, failed: 0 });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // === DATA FETCHING ===

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, deliveryRes] = await Promise.all([
        api.get('/admin/sms/stats'),
        api.get('/admin/sms/delivery-stats')
      ]);
      
      setStats(statsRes.data);
      setDeliveryStats(deliveryRes.data);
    } catch (err) {
      console.error('Failed to fetch SMS stats:', err);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get('/admin/sms/templates');
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  }, []);

  const fetchScheduledSMS = useCallback(async () => {
    try {
      const res = await api.get('/admin/sms/scheduled');
      setScheduledSMS(res.data.scheduled || []);
    } catch (err) {
      console.error('Failed to fetch scheduled SMS:', err);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const [historyRes, batchRes] = await Promise.all([
        api.get('/admin/sms/history?limit=50'),
        api.get('/admin/sms/batches?limit=10')
      ]);
      
      setHistory(historyRes.data.messages || []);
      setHistoryTotal(historyRes.data.total || 0);
      setBatches(batchRes.data.batches || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    await Promise.all([
      fetchStats(),
      fetchTemplates(),
      fetchScheduledSMS(),
      fetchHistory()
    ]);
  }, [fetchStats, fetchTemplates, fetchScheduledSMS, fetchHistory]);

  useEffect(() => {
    if (authHeaders?.Authorization) {
      fetchAll();
    }
  }, [authHeaders, fetchAll]);

  // === ACTION HANDLERS ===

  const previewRecipients = async () => {
    setLoading(true);
    try {
      let params = `recipient_type=${recipientType}`;
      if (recipientType === 'campaign' && campaignId !== 'all') {
        params += `&campaign_id=${campaignId}`;
      }
      if (recipientType === 'individual') {
        params += `&phone=${encodeURIComponent(phoneNumber)}`;
      }
      
      const res = await api.get(`/admin/sms/preview-recipients?${params}`);
      setPreviewData(res.data);
      setShowConfirmDialog(true);
    } catch (err) {
      toast.error('Failed to preview recipients');
    }
    setLoading(false);
  };

  const sendSMS = async () => {
    setSending(true);
    try {
      const payload = {
        message,
        recipient_type: recipientType,
        campaign_id: recipientType === 'campaign' && campaignId !== 'all' ? parseInt(campaignId) : null,
        phone_number: recipientType === 'individual' ? phoneNumber : null
      };
      
      const res = await api.post('/admin/sms/send', payload);
      toast.success(`SMS sent to ${res.data.sent_count} recipient${res.data.sent_count !== 1 ? 's' : ''}`);
      setMessage('');
      setShowConfirmDialog(false);
      fetchStats();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send SMS');
    }
    setSending(false);
  };

  const sendTestSMS = async () => {
    if (!phoneNumber.trim() || !message.trim()) {
      toast.error('Phone number and message required');
      return;
    }
    
    setSending(true);
    try {
      const payload = {
        message,
        recipient_type: 'individual',
        phone_number: phoneNumber
      };
      
      await api.post('/admin/sms/send', payload);
      toast.success('Test SMS sent!');
      fetchStats();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test SMS');
    }
    setSending(false);
  };

  const scheduleSMS = async () => {
    if (!message.trim() || !scheduleDateTime) {
      toast.error('Message and scheduled time required');
      return;
    }
    
    setIsScheduling(true);
    try {
      const payload = {
        message,
        recipient_type: recipientType,
        campaign_id: recipientType === 'campaign' && campaignId !== 'all' ? parseInt(campaignId) : null,
        phone_number: recipientType === 'individual' ? phoneNumber : null,
        scheduled_time: new Date(scheduleDateTime).toISOString()
      };
      
      await api.post('/admin/sms/schedule', payload);
      toast.success('SMS scheduled successfully!');
      setMessage('');
      setScheduleDateTime('');
      fetchScheduledSMS();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to schedule SMS');
    }
    setIsScheduling(false);
  };

  const cancelScheduledSMS = async (id) => {
    if (!window.confirm('Cancel this scheduled broadcast?')) return;
    
    try {
      await api.post(`/admin/sms/scheduled/${id}/cancel`);
      toast.success('Scheduled SMS cancelled');
      fetchScheduledSMS();
    } catch (err) {
      toast.error('Failed to cancel');
    }
  };

  // === TEMPLATE HANDLERS ===

  const openTemplateDialog = (template) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        name: template.name,
        content: template.content,
        category: template.category
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({ name: '', content: '', category: 'general' });
    }
    setShowTemplateDialog(true);
  };

  const saveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.content.trim()) {
      toast.error('Name and content required');
      return;
    }
    
    try {
      if (editingTemplate) {
        await api.put(`/admin/sms/templates/${editingTemplate.id}`, templateForm);
      } else {
        await api.post('/admin/sms/templates', templateForm);
      }
      toast.success(`Template ${editingTemplate ? 'updated' : 'created'}`);
      setShowTemplateDialog(false);
      fetchTemplates();
    } catch (err) {
      toast.error('Failed to save template');
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    
    try {
      await api.delete(`/admin/sms/templates/${id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (err) {
      toast.error('Failed to delete template');
    }
  };

  const applyTemplate = (template) => {
    setMessage(template.content);
    toast.success(`Applied: ${template.name}`);
  };

  // === RENDER ===

  return (
    <div className="space-y-6" data-testid="sms-tab">
      {/* Stats Header */}
      <SMSStatsHeader
        stats={stats}
        deliveryStats={deliveryStats}
        scheduledCount={scheduledSMS.filter(s => s.status === 'scheduled').length}
      />

      {/* Navigation Tabs */}
      <div className="flex gap-2 border-b border-border pb-2">
        {[
          { id: 'compose', label: 'Compose', icon: Send },
          { id: 'templates', label: 'Templates', icon: FileText },
          { id: 'scheduled', label: 'Scheduled', icon: Calendar },
          { id: 'history', label: 'History', icon: Clock }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveView(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeView === tab.id 
                ? 'bg-primary text-primary-foreground' 
                : 'text-muted-foreground hover:bg-muted'
            }`}
            data-testid={`sms-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Compose View */}
      {activeView === 'compose' && (
        <SMSComposeView
          message={message}
          setMessage={setMessage}
          recipientType={recipientType}
          setRecipientType={setRecipientType}
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          campaignId={campaignId}
          setCampaignId={setCampaignId}
          templates={templates}
          loading={loading}
          sending={sending}
          onPreviewRecipients={previewRecipients}
          onSendTestSMS={sendTestSMS}
          onOpenTemplateDialog={openTemplateDialog}
          onApplyTemplate={applyTemplate}
        />
      )}

      {/* Templates View */}
      {activeView === 'templates' && (
        <SMSTemplatesView
          templates={templates}
          onOpenTemplateDialog={openTemplateDialog}
          onDeleteTemplate={deleteTemplate}
          onApplyTemplate={applyTemplate}
          onSwitchToCompose={() => setActiveView('compose')}
        />
      )}

      {/* Scheduled View */}
      {activeView === 'scheduled' && (
        <SMSScheduledView
          scheduledSMS={scheduledSMS}
          message={message}
          setMessage={setMessage}
          recipientType={recipientType}
          setRecipientType={setRecipientType}
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          campaignId={campaignId}
          setCampaignId={setCampaignId}
          scheduleDateTime={scheduleDateTime}
          setScheduleDateTime={setScheduleDateTime}
          isScheduling={isScheduling}
          onScheduleSMS={scheduleSMS}
          onCancelScheduledSMS={cancelScheduledSMS}
          onRefresh={fetchScheduledSMS}
        />
      )}

      {/* History View */}
      {activeView === 'history' && (
        <SMSHistoryView
          history={history}
          historyTotal={historyTotal}
          batches={batches}
          onRefresh={fetchHistory}
        />
      )}

      {/* Dialogs */}
      <SMSConfirmDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        previewData={previewData}
        recipientType={recipientType}
        phoneNumber={phoneNumber}
        campaignId={campaignId}
        message={message}
        sending={sending}
        onSend={sendSMS}
      />

      <SMSTemplateDialog
        open={showTemplateDialog}
        onOpenChange={setShowTemplateDialog}
        editingTemplate={editingTemplate}
        templateForm={templateForm}
        setTemplateForm={setTemplateForm}
        onSave={saveTemplate}
      />
    </div>
  );
}
